import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Details,
  FlexGrid,
  FlexGridColumn,
  Icon,
  Summary,
  Text,
  uniqueId,
} from '@hs-baumappe/legacy-ui';
import { useFormikContext } from 'formik';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import OvertimeUploadImageWidgetEntry from './OvertimeUploadImageWidgetEntry';

import {
  createEmptyImageWithLabel,
  ImageUploadWithLabelFormValues,
} from './OvertimeUploadImageWidgetEntry/components/ImageUploadWithLabel';
import OvertimeConvertImageAttachmentsMutation from './graphql/OvertimeConvertImageAttachments.mutation';
import ErrorState from '../../../ErrorState';
import DriveImageGalleryContainer, {
  DriveImageGalleryDocument,
} from '../../../DriveImageGalleryContainer';

export interface OvertimeUploadImageWidgetFormValues {
  images: ImageUploadWithLabelFormValues[];
}

interface OvertimeUploadImageWidgetProps {
  projectId: string;
}

export default function OvertimeUploadImageWidget({
  projectId,
}: OvertimeUploadImageWidgetProps): JSX.Element {
  const { t } = useTranslation();
  const imagesTitleId = useRef(uniqueId('images-title-'));

  const [driveImageGalleryOpen, setDriveImageGalleryOpen] = useState(false);
  const formik = useFormikContext<OvertimeUploadImageWidgetFormValues>();
  const [convertDriveImagesMutation, { loading, error }] = useMutation(
    OvertimeConvertImageAttachmentsMutation,
  );

  const { values, setFieldValue, initialValues } = formik;

  function removeEntryById(index: number) {
    const images = [...values.images];

    images.splice(index, 1);

    setFieldValue('images', images);
  }

  async function convertImages(selectedDocuments: string[]) {
    const { data } = await convertDriveImagesMutation({
      variables: {
        input: selectedDocuments,
      },
    });

    if (!data) {
      return;
    }

    return data.convertCustomFilesToImages;
  }

  async function handleDocumentsSelected(selectedDocuments: DriveImageGalleryDocument[]) {
    setDriveImageGalleryOpen(false);

    const documentIds = selectedDocuments.map(({ id }) => id);
    const convertedImages = await convertImages(documentIds);

    if (!convertedImages) {
      return;
    }

    const imageAttachments: ImageUploadWithLabelFormValues[] = convertedImages.map(
      (convertedImage, index) => {
        const selectedDocument = selectedDocuments[index];

        return createEmptyImageWithLabel({
          imageId: convertedImage.id,
          label: selectedDocument?.name || '',
          imageUrl: convertedImage.url,
          imageThumbnailUrl: convertedImage.url,
        });
      },
    );

    setFieldValue('images', [...values.images, ...imageAttachments]);
    setDriveImageGalleryOpen(false);
  }

  const hasImageAttachmentsOnInitial = initialValues.images.length > 0;
  const hasImageAttachments = values.images.length > 0;
  const showRemoveButton = values.images.length > 1;

  return (
    <Details
      className="qa-overtime-create-upload-image"
      defaultOpen={hasImageAttachmentsOnInitial}
      aria-labelledby={imagesTitleId.current}
      renderSummary={({ onClick }) => (
        <Summary id={imagesTitleId.current} onClick={onClick}>
          <Text variant="title-small" color="muted" component="h3" className="u-margin-bottom-0">
            {t('overtimeUploadImageWidget.title')}
          </Text>
        </Summary>
      )}
    >
      {error && <ErrorState error={error} />}

      <FlexGrid>
        <FlexGridColumn sm={5}>
          <Text component="p">{t('overtimeUploadImageWidget.helperText')}</Text>
        </FlexGridColumn>
      </FlexGrid>

      <Box stylex={{ marginTop: 'medium' }}>
        {values.images.map((image, index) => {
          return (
            <OvertimeUploadImageWidgetEntry
              name={`images.${index}`}
              key={image.orderId}
              onRequestRemove={() => removeEntryById(index)}
              showRemoveButton={showRemoveButton}
            />
          );
        })}
      </Box>

      <FlexGrid>
        <FlexGridColumn sm={5}>
          <Button
            onClick={() => setDriveImageGalleryOpen(true)}
            color="default"
            endIcon={<Icon name={hasImageAttachments ? 'add' : 'add-a-photo'} />}
            type="button"
            fullWidth
            loading={loading}
          >
            {hasImageAttachments
              ? t('overtimeUploadImageWidget.addAnotherImageButton')
              : t('overtimeUploadImageWidget.addMultipleImagesButton')}
          </Button>
        </FlexGridColumn>
      </FlexGrid>

      <DriveImageGalleryContainer
        open={driveImageGalleryOpen}
        projectId={projectId}
        onDialogClose={() => setDriveImageGalleryOpen(false)}
        onDocumentsSelected={handleDocumentsSelected}
      />
    </Details>
  );
}
